import { useState } from 'react'

import { getRoutes } from '@/routeRegistry'
import { DISABLE_AUTH, ENABLE_INLINE_EDIT } from '@/lib/flags'
import { useAuth } from '@/lib/auth-context'

const safeGet = (key: string, fallback: string): string => {
  if (typeof window === 'undefined') return fallback
  try {
    const value = window.localStorage.getItem(key)
    return value ?? fallback
  } catch {
    return fallback
  }
}

export function OverlaySwitch() {
  const [enabled, setEnabled] = useState(() => safeGet('overlay.enabled', 'auto'))
  const [pack, setPack] = useState(() => safeGet('overlay.pack', 'auto'))
  const [intensity, setIntensity] = useState(() => safeGet('overlay.intensity', 'med'))
  const [border, setBorder] = useState(() => safeGet('overlay.border', 'auto'))

  const apply = () => {
    const envDefault = import.meta.env.VITE_OVERLAYS_ENABLED === 'true' ? 'true' : 'false'
    window.localStorage.setItem('overlay.enabled', enabled === 'auto' ? envDefault : enabled)
    window.localStorage.setItem('overlay.pack', pack)
    window.localStorage.setItem('overlay.intensity', intensity)
    if (border === 'auto') {
      window.localStorage.removeItem('overlay.border')
    } else {
      window.localStorage.setItem('overlay.border', border)
    }
    window.location.reload()
  }

  const reset = () => {
    window.localStorage.removeItem('overlay.enabled')
    window.localStorage.removeItem('overlay.pack')
    window.localStorage.removeItem('overlay.intensity')
    window.localStorage.removeItem('overlay.border')
    window.location.reload()
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <label>Enabled:</label>
      <select value={enabled} onChange={(e) => setEnabled(e.target.value)}>
        <option value="true">Enabled</option>
        <option value="false">Disabled</option>
        <option value="auto">Env default</option>
      </select>

      <label>Pack:</label>
      <select value={pack} onChange={(e) => setPack(e.target.value)}>
        <option value="auto">Auto by month</option>
        <option value="fall">Fall leaves</option>
        <option value="winter">Winter snow</option>
        <option value="none">None</option>
      </select>

      <label>Intensity:</label>
      <select value={intensity} onChange={(e) => setIntensity(e.target.value)}>
        <option value="low">low</option>
        <option value="med">med</option>
        <option value="high">high</option>
      </select>

      <label>Border:</label>
      <select value={border} onChange={(e) => setBorder(e.target.value)}>
        <option value="auto">Server default</option>
        <option value="none">None</option>
        <option value="aurora">Aurora</option>
        <option value="frost">Frost</option>
        <option value="harvest">Harvest</option>
        <option value="sparkle">Sparkle</option>
      </select>

      <button className="badge bg-brick text-white" onClick={apply} type="button">
        Apply
      </button>
      <button className="badge" onClick={reset} type="button">
        Reset
      </button>
    </div>
  )
}

export default function DevTools() {
  const { role } = useAuth()
  const routes = getRoutes()

  return (
    <div className="max-w-5xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-4">Dev Tools</h1>

      <div className="mb-6 rounded-xl border p-4 bg-white">
        <div className="font-mono text-sm">
          <div>
            DISABLE_AUTH: <b>{String(DISABLE_AUTH)}</b>
          </div>
          <div>
            ENABLE_INLINE_EDIT: <b>{String(ENABLE_INLINE_EDIT)}</b>
          </div>
        </div>
      </div>

      {role === 'admin' && (
        <div className="mb-6 rounded-xl border p-4 bg-white">
          <h2 className="text-lg font-semibold mb-2">Overlay switch</h2>
          <OverlaySwitch />
        </div>
      )}

      <h2 className="text-lg font-semibold mb-2">All routes</h2>
      <ul className="space-y-2">
        {routes.map((r, i) => (
          <li key={i}>
            <a className="text-cyan-700 underline" href={r.path}>
              {r.label || r.path}
            </a>
            <span className="ml-2 text-cocoa/60 font-mono">{r.path}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
